import * as React from 'react'
import { useIntl } from 'react-intl'
import dayjs from "dayjs"
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { GeneralPromocodeState } from '../../../../features/types'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { promocodeTypes } from '../../../../utils/constants'
import { iconButton, activeIconButton } from '../style'

interface Props {
    promocodeData: GeneralPromocodeState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
    onDelete: (id: number) => void
}

const PromocodeTable: React.FC<Props> = ({
    promocodeData = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage,
    isFetching,
    onDelete,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage, locale } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, rowsPerPage - promocodeData.length) : 0

    // const emptyRows =
    // page > 0 ? Math.max(0, (1 + page) * rowsPerPage - teachersData.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    const getType = (type) => {
        return promocodeTypes({formatMessage}).find(item => item?.value === type)?.title || ""
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'code' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'description' })}
                            </TableCell>

                            <TableCell>
                                Аяқталу күні
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'maxInvoiceAmount' })}
                            </TableCell>

                            <TableCell>
                                Қолдану шегі
                            </TableCell>

                            <TableCell>
                                {formatMessage({
                                    id: 'minInvoiceAmount',
                                })}
                            </TableCell>

                            <TableCell>
                                promoValue
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'type' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {promocodeData.length ? (
                        <TableBody>
                            {promocodeData.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.code}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.description}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {dayjs(row?.expiresAt)?.format("DD.MM.YYYY")}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row?.maxInvoiceAmount
                                        }
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.maxUsage}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.minInvoiceAmount}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.promoValue}
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        {getType(row?.type)}
                                    </TableCell>

                                    <TableCell
                                        align="right"
                                    >
                                        <ConfirmDelete
                                            confirmAction={() => onDelete(row?.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={10} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={10}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default PromocodeTable
